import React, { useState, useEffect } from 'react';
import { Award, Search, X, Plus, Save, Loader } from 'lucide-react';
import styles from './AwardCommendation.module.css';
import axios from '../api/axios';

interface User {
  id: number;
  username: string;
  discordDisplayName?: string;
}

interface Mission {
  id: number;
  title: string;
}

interface Event {
  id: number;
  title: string;
}

interface CommendationType {
  value: number;
  name: string;
  title: string;
  category: number;
  categoryName: string;
}

interface CommendationCategory {
  value: number;
  name: string;
}

interface CreateCommendationDto {
  memberId: number;
  type: number;
  customTitle?: string;
  description: string;
  missionId?: number;
  eventId?: number;
  iconUrl?: string;
  metricsData?: string;
  isPublic: boolean;
}

export const AwardCommendation: React.FC = () => {
  const [members, setMembers] = useState<User[]>([]);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [types, setTypes] = useState<CommendationType[]>([]);
  const [categories, setCategories] = useState<CommendationCategory[]>([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<User | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateCommendationDto>({
    memberId: 0,
    type: 0,
    description: '',
    isPublic: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedMember) {
      setFormData(prev => ({ ...prev, memberId: selectedMember.id }));
    }
  }, [selectedMember]);

  useEffect(() => {
    if (formData.type && formData.type !== 0) {
      const selectedType = types.find(t => t.value === formData.type);
      if (selectedType) {
        // Icon path format: /commendation-icons/{TypeName}.png
        // For now, using placeholder until icons are uploaded
        const iconPath = `https://commendation-icons.tficorg.org/${selectedType.name}.png`;
        // Fallback to placeholder SVG data URI with ribbon icon if R2 not configured
        const placeholderIcon = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM0ZjQ2ZTUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48Y2lyY2xlIGN4PSIxMiIgY3k9IjgiIHI9IjYiLz48cGF0aCBkPSJNMTUuNDc3IDEyLjg5IDEyIDE1bC0zLjQ3Ny0yLjExdjYuMjFsLjA5NiAxLjg0IDMuMzgxIDEuNjkxIDMuMzgxLTEuNjkxLjA5Ni0xLjg0eiIvPjwvc3ZnPg==';
        setFormData(prev => ({ ...prev, iconUrl: iconPath }));
      }
    }
  }, [formData.type, types]);

  const fetchData = async () => {
    try {
      const [typesRes, categoriesRes, missionsRes, eventsRes] = await Promise.all([
        axios.get('/commendations/types'),
        axios.get('/commendations/categories'),
        axios.get('/missions/all'),
        axios.get('/events'),
      ]);

      console.log('Types response:', typesRes.data);
      console.log('Categories response:', categoriesRes.data);
      console.log('Missions response:', missionsRes.data);
      console.log('Events response:', eventsRes.data);

      setTypes(typesRes.data || []);
      setCategories(categoriesRes.data || []);
      setMissions(missionsRes.data || []);
      setEvents(eventsRes.data || []);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load data');
    }
  };

  const searchMembers = async (query: string) => {
    if (query.length < 2) {
      setMembers([]);
      return;
    }

    try {
      const response = await axios.get(`/auth/user/search?query=${encodeURIComponent(query)}`);
      setMembers(response.data);
    } catch (err) {
      console.error('Error searching members:', err);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    searchMembers(value);
  };

  const handleMemberSelect = (member: User) => {
    setSelectedMember(member);
    setSearchQuery('');
    setMembers([]);
  };

  const handleCategoryFilter = (categoryValue: number) => {
    setSelectedCategory(categoryValue === selectedCategory ? null : categoryValue);
  };

  const filteredTypes = selectedCategory
    ? types.filter(t => t.category === selectedCategory)
    : types;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedMember) {
      setError('Please select a member');
      return;
    }

    if (!formData.type) {
      setError('Please select a commendation type');
      return;
    }

    if (!formData.description.trim()) {
      setError('Please provide a description');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await axios.post('/commendations', formData);

      setSuccess(true);
      // Reset form
      setFormData({
        memberId: 0,
        type: 0,
        description: '',
        isPublic: true,
      });
      setSelectedMember(null);
      setSelectedCategory(null);

      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <Award className={styles.titleIcon} />
          Award Commendation
        </h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Member Search */}
        <div className={styles.formGroup}>
          <label className={styles.label} title="Search and select the organization member who will receive this commendation">Member *</label>
          {selectedMember ? (
            <div className={styles.selectedMember}>
              <span>{selectedMember.username}</span>
              <button
                type="button"
                onClick={() => setSelectedMember(null)}
                className={styles.clearButton}
              >
                <X />
              </button>
            </div>
          ) : (
            <>
              <div className={styles.searchBox}>
                <Search className={styles.searchIcon} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by username..."
                  className={styles.searchInput}
                />
              </div>
              {members.length > 0 && (
                <ul className={styles.searchResults}>
                  {members.map(member => (
                    <li
                      key={member.id}
                      onClick={() => handleMemberSelect(member)}
                      className={styles.searchResultItem}
                    >
                      <span className={styles.resultUsername}>{member.username}</span>
                      {member.discordDisplayName && (
                        <span className={styles.resultDisplayName}>({member.discordDisplayName})</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        {/* Category Filter */}
        <div className={styles.formGroup}>
          <label className={styles.label} title="Filter commendation types by category to narrow your selection (optional)">Category (optional filter)</label>
          <div className={styles.categoryButtons}>
            {categories.map(cat => (
              <button
                key={cat.value}
                type="button"
                onClick={() => handleCategoryFilter(cat.value)}
                className={`${styles.categoryButton} ${selectedCategory === cat.value ? styles.active : ''}`}
              >
                {cat.name.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Commendation Type */}
        <div className={styles.formGroup}>
          <label className={styles.label} title="Select the specific type of commendation to award. Each type represents a different achievement or recognition.">Commendation Type *</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: parseInt(e.target.value) })}
            className={styles.select}
            required
          >
            <option value={0}>Select a commendation type...</option>
            {filteredTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.title.replace(/([A-Z])/g, ' $1').trim()} ({type.categoryName.replace(/_/g, ' ')})
              </option>
            ))}
          </select>
        </div>

        {/* Custom Title */}
        <div className={styles.formGroup}>
          <label className={styles.label} title="Override the default commendation title with a custom one. Leave blank to use the default title for this commendation type.">Custom Title (optional)</label>
          <input
            type="text"
            value={formData.customTitle || ''}
            onChange={(e) => setFormData({ ...formData, customTitle: e.target.value })}
            placeholder="Leave blank for default title"
            className={styles.input}
          />
        </div>

        {/* Description */}
        <div className={styles.formGroup}>
          <label className={styles.label} title="Provide details about why this commendation is being awarded, including specific actions, achievements, or contributions.">Description *</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe why this commendation is being awarded..."
            className={styles.textarea}
            rows={4}
            required
          />
        </div>

        {/* Mission (optional) */}
        <div className={styles.formGroup}>
          <label className={styles.label} title="Link this commendation to a specific mission if the achievement occurred during mission operations.">Related Mission (optional)</label>
          <select
            value={formData.missionId || ''}
            onChange={(e) => setFormData({ ...formData, missionId: e.target.value ? parseInt(e.target.value) : undefined })}
            className={styles.select}
          >
            <option value="">None</option>
            {missions.slice(0, 50).map(mission => (
              <option key={mission.id} value={mission.id}>
                {mission.title}
              </option>
            ))}
          </select>
        </div>

        {/* Event (optional) */}
        <div className={styles.formGroup}>
          <label className={styles.label} title="Link this commendation to a specific event if the achievement occurred during an organization event.">Related Event (optional)</label>
          <select
            value={formData.eventId || ''}
            onChange={(e) => setFormData({ ...formData, eventId: e.target.value ? parseInt(e.target.value) : undefined })}
            className={styles.select}
          >
            <option value="">None</option>
            {events.slice(0, 50).map(event => (
              <option key={event.id} value={event.id}>
                {event.title}
              </option>
            ))}
          </select>
        </div>

        {/* Metrics Data */}
        <div className={styles.formGroup}>
          <label className={styles.label} title="Add quantifiable data or statistics related to this commendation (e.g., number of missions completed, cargo hauled, credits earned).">Metrics Data (optional)</label>
          <input
            type="text"
            value={formData.metricsData || ''}
            onChange={(e) => setFormData({ ...formData, metricsData: e.target.value })}
            placeholder='e.g., "5 missions completed" or "10,000 UEC earned"'
            className={styles.input}
          />
        </div>

        {/* Public Visibility */}
        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel} title="When checked, this commendation will be visible to all organization members. Uncheck for private commendations visible only to leadership.">
            <input
              type="checkbox"
              checked={formData.isPublic}
              onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
              className={styles.checkbox}
            />
            <span>Public (visible to all users)</span>
          </label>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}
        {success && (
          <div className={styles.successMessage}>
            Commendation awarded successfully!
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={styles.submitButton}
        >
          {loading ? (
            <>
              <Loader className={styles.spinner} />
              Awarding...
            </>
          ) : (
            <>
              <Save />
              Award Commendation
            </>
          )}
        </button>
      </form>
    </div>
  );
};
